import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Download } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  integrityApi,
  practiceCoveragePct,
  type IntegrityComposition,
  type IntegrityCoverage,
  type IntegrityHandlingCounts,
  type IntegritySummary,
} from '@/lib/api/integrity';
import { cn } from '@/lib/cn';
import { useOrgOverview } from '@/lib/hooks/useOrg';
import { downloadIntegrityReport } from './integrityReport';

/**
 * What the readiness numbers above are built on.
 *
 * Placed inside Placement Readiness rather than in a tab of its own. Integrity
 * data's job here is to make a claim credible, not to be a subject — and a
 * screen labelled "Integrity" invites "so who was it?" on every number, which
 * is the reading this whole design is arranged to avoid.
 *
 * Three blocks, and the order carries the argument: what we can see, then what
 * the record shows, then what was done about it. Coverage comes first because
 * publishing our own blind spot is the cheapest credibility available — every
 * competing dashboard reports total confidence about things it cannot see, and
 * a college that catches us overstating once will discount everything after it.
 */

/** Below this the strip changes tone: it cannot support much weight yet. */
const THIN_COVERAGE_PCT = 45;

export function VerificationStrip({ bands = [] }: { bands?: { label: string; count: number }[] }) {
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['org', 'integrity', 'summary'],
    queryFn: async () => (await integrityApi.getSummary()).data,
  });

  if (isLoading) return <Skeleton className="h-11 w-full" />;
  // Silent on failure. This is supporting evidence for the bands below it, and
  // an error banner over someone else's screen helps nobody.
  if (isError || !data) return null;

  const pct = practiceCoveragePct(data.coverage);
  const thin = pct < THIN_COVERAGE_PCT;

  return (
    <Card
      className={cn(
        'overflow-hidden shadow-none',
        thin ? 'border-amber-200 bg-amber-50/60' : 'border-sky-200 bg-sky-50/60',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-xs hover:bg-white/50',
          thin ? 'text-amber-900' : 'text-sky-900',
        )}
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 flex-none opacity-60 transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          {/*
            Low coverage is not an error state — it is the strip admitting it
            cannot support much weight, which is the case a college meets in
            month one. If that reading is not legible then the honest framing
            fails exactly when it matters most.
          */}
          {thin ? (
            <>
              <b className="font-semibold">Limited record so far</b> — we can account for only{' '}
              <b className="font-semibold">{Math.round(pct)}%</b> of practice work. Treat the
              figures below as partial.
            </>
          ) : (
            <>
              Verified against recorded work for <b className="font-semibold">{Math.round(pct)}%</b>{' '}
              of practice and{' '}
              <b className="font-semibold">
                {data.coverage.repos_read} of {data.coverage.repos_expected}
              </b>{' '}
              repositories
            </>
          )}
          <HandlingLine handling={data.handling} />
        </span>
      </button>

      {open && <StripBody summary={data} bands={bands} />}
    </Card>
  );
}

/**
 * The chain, compressed to one clause for the collapsed line.
 *
 * "Waiting for you" rather than "raised to you", which block C already uses for
 * a different number — escalations raised in the window, against escalations
 * still unacknowledged. Two different counts under one label on one screen is
 * how a reader learns to trust neither.
 */
function HandlingLine({ handling }: { handling: IntegrityHandlingCounts }) {
  if (handling.raised === 0) return null;
  return (
    <>
      {' · '}
      <b className="font-semibold">{handling.raised}</b> concerns ·{' '}
      <b className="font-semibold">{handling.resolved}</b> resolved as nothing
      {handling.unresolved > 0 && (
        <>
          {' · '}
          <b className="font-semibold">{handling.unresolved}</b> waiting for you
        </>
      )}
    </>
  );
}

function StripBody({
  summary,
  bands,
}: {
  summary: IntegritySummary;
  bands: { label: string; count: number }[];
}) {
  return (
    <>
      <div className="grid gap-3 px-3.5 pb-3.5 md:grid-cols-3">
        <Block letter="A" title="What we can see">
          <CoverageBlock coverage={summary.coverage} />
        </Block>
        <Block letter="B" title="What the record shows">
          <CompositionBlock composition={summary.composition} />
        </Block>
        <Block letter="C" title="What was done about it">
          <HandlingBlock handling={summary.handling} sinceDays={summary.since_days} />
        </Block>
      </div>
      <ExportRow summary={summary} bands={bands} />
    </>
  );
}

/**
 * Downloads the whole strip as one file the TPO can forward.
 *
 * Names are a checkbox and it is off. This file leaves the platform: a report
 * that carries three students' names by default will one day reach a group
 * chat, and no careful wording on page one survives that. Making inclusion a
 * decision somebody took is the only footing on which names should travel.
 */
function ExportRow({
  summary,
  bands,
}: {
  summary: IntegritySummary;
  bands: { label: string; count: number }[];
}) {
  const [withNames, setWithNames] = useState(false);
  // The org's own name for the report header. Already cached by the shell.
  const { data: overview } = useOrgOverview();
  const orgName = overview?.org_name ?? 'Your college';

  // Only fetched when the box is ticked, so the default path never pulls named
  // records it is not going to use.
  const { data: escalations } = useQuery({
    queryKey: ['org', 'escalations', 'all'],
    queryFn: async () => (await integrityApi.listEscalations(true)).data,
    enabled: withNames,
  });

  return (
    <div className="flex flex-wrap items-center gap-3 px-3.5 pb-3.5">
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 bg-background px-2.5 text-xs"
        onClick={() =>
          downloadIntegrityReport({
            orgName,
            summary,
            bands,
            escalations: withNames ? escalations : undefined,
          })
        }
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        Download report
      </Button>

      <Label className="flex cursor-pointer items-center gap-2 text-xs font-normal text-muted-foreground">
        <Checkbox
          checked={withNames}
          onCheckedChange={(v) => setWithNames(v === true)}
          aria-label="Include student names in the downloaded report"
        />
        Include student names
      </Label>

      {withNames && (
        <span className="text-[11px] text-amber-700">
          The file will name students. It is meant to be forwarded — send it carefully.
        </span>
      )}
    </div>
  );
}

function Block({
  letter,
  title,
  children,
}: {
  letter: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-background p-3">
      <p className="mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[10px] font-bold">
          {letter}
        </span>
        {title}
      </p>
      {children}
    </div>
  );
}

function CoverageBlock({ coverage }: { coverage: IntegrityCoverage }) {
  const pct = practiceCoveragePct(coverage);
  return (
    <>
      <p className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold tabular-nums tracking-tight">{Math.round(pct)}%</span>
        <span className="text-xs text-muted-foreground">of practice work</span>
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {coverage.practice_recorded.toLocaleString()} of{' '}
        {coverage.practice_attempts.toLocaleString()} submissions have a recorded process
      </p>
      <p className="mt-2.5 flex items-baseline gap-1.5">
        <span className="text-2xl font-bold tabular-nums tracking-tight">
          {coverage.repos_read}
        </span>
        <span className="text-xs text-muted-foreground">
          of {coverage.repos_expected} repositories read
        </span>
      </p>
      {/*
        Named explicitly rather than left as the difference between two numbers.
        "What we cannot see" is the sentence that makes the rest believable, and
        arithmetic the reader has to do themselves is not a disclosure.
      */}
      <p className="mt-2.5 text-[11px] leading-relaxed text-muted-foreground">
        <span className="font-semibold text-foreground">What we can’t see:</span> work done before
        this was switched on, all quizzes, private repositories, and hand-ins that weren’t GitHub
        links.
      </p>
    </>
  );
}

function CompositionBlock({ composition: c }: { composition: IntegrityComposition }) {
  const practice = c.written_here + c.part_pasted + c.arrived_whole;
  const repos = c.built_over_time + c.one_sitting + c.one_commit;

  return (
    <>
      {practice > 0 && (
        <>
          <p className="text-xs text-muted-foreground">Practice</p>
          <div className="my-1.5 flex h-1.5 overflow-hidden rounded-full bg-muted">
            <span
              className="bg-emerald-500"
              style={{ width: `${(c.written_here / practice) * 100}%` }}
            />
            <span
              className="bg-blue-500"
              style={{ width: `${(c.part_pasted / practice) * 100}%` }}
            />
            <span
              className="bg-sky-800"
              style={{ width: `${(c.arrived_whole / practice) * 100}%` }}
            />
          </div>
          <Legend
            items={[
              ['bg-emerald-500', 'Written here', c.written_here],
              ['bg-blue-500', 'Part pasted', c.part_pasted],
              ['bg-sky-800', 'Arrived whole', c.arrived_whole],
            ]}
          />
        </>
      )}

      {repos > 0 && (
        <>
          <p className="mt-3 text-xs text-muted-foreground">Repositories</p>
          <Legend
            items={[
              ['bg-emerald-500', 'Built over time', c.built_over_time],
              ['bg-muted-foreground', 'One sitting', c.one_sitting],
              ['bg-sky-800', 'One commit', c.one_commit],
            ]}
          />
        </>
      )}

      {c.similar_groups > 0 && (
        <p className="mt-2.5 text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground">{c.similar_groups}</span> students share
          distinctive code with a classmate.
        </p>
      )}

      {practice === 0 && repos === 0 && (
        <p className="text-xs text-muted-foreground">Nothing recorded yet.</p>
      )}
    </>
  );
}

function Legend({ items }: { items: [string, string, number][] }) {
  return (
    <ul className="space-y-1 text-xs">
      {items.map(([tone, label, n]) => (
        <li key={label} className="flex items-center gap-2">
          <span className={cn('h-2 w-2 flex-none rounded-sm', tone)} />
          {label}
          <span className="ml-auto tabular-nums text-muted-foreground">{n.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}

function HandlingBlock({
  handling: h,
  sinceDays,
}: {
  handling: IntegrityHandlingCounts;
  sinceDays: number;
}) {
  if (h.raised === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Nothing has needed handling in the last {sinceDays} days.
      </p>
    );
  }

  return (
    <>
      <Step n={h.raised} label="concerns raised" />
      <Step n={h.resolved} label="resolved as nothing" dim />
      <Step n={h.escalated} label="raised to you" highlight />
      {/*
        Distinct from the line above it: how many of those raised are still
        unread, which is the only number here that is a task rather than a
        record.
      */}
      {h.unresolved > 0 && <Step n={h.unresolved} label="still waiting for you" dim />}
      {/*
        The sentence that stops a percentage being read as an accusation count.
        Stated rather than left to be inferred, because the inference a worried
        administrator makes unaided is the other one.
      */}
      <p className="mt-2.5 text-[11px] leading-relaxed text-muted-foreground">
        Most flags turn out to be nothing. That is the system working — a signal that were always
        right would be one nobody needed a person for.
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground/70">Last {sinceDays} days.</p>
    </>
  );
}

function Step({
  n,
  label,
  dim,
  highlight,
}: {
  n: number;
  label: string;
  dim?: boolean;
  highlight?: boolean;
}) {
  return (
    <p
      className={cn(
        'flex items-center gap-2 py-0.5 text-xs',
        dim && 'text-muted-foreground',
        highlight && 'font-semibold text-sky-800',
      )}
    >
      <span className="min-w-[26px] text-base font-bold tabular-nums">{n}</span>
      {label}
    </p>
  );
}
